import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Plus, Trash2, Save, Download, Search, Globe, RefreshCw } from "lucide-react";
import { listCourseLibrary, createLibraryCourse, updateLibraryCourse, deleteLibraryCourse, searchCoursesOnline, fetchCourseDetails } from "@/lib/api";
import { uid } from "@/lib/constants";
import { syncTripCoursesFromLibrary } from "@/lib/courseSync";
import CourseUpdatePanel from "@/screens/CourseUpdatePanel";

/**
 * Library modal: view/manage the global course library and import a tee box into the current trip.
 * Props:
 *  - open, onClose
 *  - eventCourses (object) — the trip's ev.courses
 *  - onImport(courseKey, entry) — called to import into trip; entry = { name, subtitle, par, si }
 *  - initialSaveCourse? — if provided, opens the "Save trip course to library" flow with prefilled data
 */
export default function CourseLibraryModal({ open, onClose, onImport, initialSaveCourse, updateEventLocal }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // library id
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSubtitle, setNewSubtitle] = useState("");
  const [newFirstTee, setNewFirstTee] = useState("Blue");
  const [webQuery, setWebQuery] = useState("");
  const [webResults, setWebResults] = useState([]);
  const [webLoading, setWebLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // library id
  const [updating, setUpdating] = useState(null); // library id with update panel open

  const load = async () => {
    setLoading(true);
    try {
      const data = await listCourseLibrary();
      setItems(data);
    } catch {
      toast.error("Could not load course library");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { load(); setShowNew(false); setExpanded(null); setConfirmDelete(null); setUpdating(null); } }, [open]);

  useEffect(() => {
    if (open && initialSaveCourse) {
      setShowNew(true);
      setNewName(initialSaveCourse.name || "");
      setNewSubtitle(initialSaveCourse.subtitle || "");
      setNewFirstTee("Default");
    }
  }, [open, initialSaveCourse]);

  if (!open) return null;

  const createNew = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Course name required"); return; }
    setSaving(true);
    try {
      const firstTee = {
        id: uid(),
        name: newFirstTee.trim() || "Default",
        par: initialSaveCourse ? [...initialSaveCourse.par] : Array(18).fill(4),
        si: initialSaveCourse ? [...initialSaveCourse.si] : Array.from({ length: 18 }, (_, i) => i + 1),
      };
      const created = await createLibraryCourse({ name, subtitle: newSubtitle, teeBoxes: [firstTee] });
      toast.success(`Saved "${name}" to library`);
      setShowNew(false);
      setNewName(""); setNewSubtitle(""); setNewFirstTee("Blue");
      await load();
      setExpanded(created.id);
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const patchItem = async (id, patch) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    const merged = { name: item.name, subtitle: item.subtitle, teeBoxes: item.teeBoxes, ...patch };
    try {
      const updated = await updateLibraryCourse(id, merged);
      setItems(prev => prev.map(x => x.id === id ? updated : x));
    } catch { toast.error("Update failed"); }
  };

  const addTeeBox = async (item) => {
    const name = window.prompt("Tee box name?", "White");
    if (!name) return;
    const tees = [...item.teeBoxes, { id: uid(), name, par: Array(18).fill(4), si: Array.from({ length: 18 }, (_, i) => i + 1) }];
    await patchItem(item.id, { teeBoxes: tees });
  };

  const deleteTeeBox = async (item, teeId) => {
    if (item.teeBoxes.length <= 1) { toast.error("At least one tee box required"); return; }
    if (!window.confirm("Delete this tee box?")) return;
    const tees = item.teeBoxes.filter(t => t.id !== teeId);
    await patchItem(item.id, { teeBoxes: tees });
  };

  const updateTeeCell = async (item, teeId, field, holeIdx, value) => {
    const tees = item.teeBoxes.map(t => {
      if (t.id !== teeId) return t;
      const arr = [...t[field]];
      arr[holeIdx] = parseInt(value) || 0;
      return { ...t, [field]: arr };
    });
    await patchItem(item.id, { teeBoxes: tees });
  };

  const updateTeeName = async (item, teeId, name) => {
    const tees = item.teeBoxes.map(t => t.id === teeId ? { ...t, name } : t);
    await patchItem(item.id, { teeBoxes: tees });
  };

  const removeCourse = async (id) => {
    try {
      await deleteLibraryCourse(id);
      setItems(prev => prev.filter(x => x.id !== id));
      setConfirmDelete(null);
      toast.success("Course deleted from library");
    } catch { toast.error("Delete failed"); }
  };

  const runWebSearch = async () => {
    const q = webQuery.trim();
    if (q.length < 2) return;
    setWebLoading(true);
    setWebResults([]);
    try {
      const data = await searchCoursesOnline(q);
      // Handle either { courses: [...] } or array
      const list = Array.isArray(data) ? data : (data.courses || []);
      setWebResults(list.slice(0, 20));
      if (list.length === 0) toast.info("No matches — try a different query");
    } catch (e) {
      toast.error("Search failed — check API key");
    } finally { setWebLoading(false); }
  };

  const importFromWeb = async (course) => {
    setImporting(course.id);
    try {
      const detail = await fetchCourseDetails(course.id);
      // GolfCourseAPI format: detail.course.tees.male / female arrays, each has {tee_name, holes:[{par, handicap, ...}]}
      const c = detail.course || detail;
      const teesRaw = (c.tees && (c.tees.male || c.tees.female || [])) || [];
      const teeBoxes = teesRaw
        .filter((t) => Array.isArray(t.holes) && t.holes.length >= 18)
        .map((t) => ({
          id: uid(),
          name: t.tee_name || "Default",
          par: t.holes.slice(0, 18).map((h) => h.par || 4),
          si: t.holes.slice(0, 18).map((h) => h.handicap || 0).map((v, i, a) => v || i + 1),
        }));
      if (teeBoxes.length === 0) {
        toast.error("This course has no hole data");
        return;
      }
      const created = await createLibraryCourse({
        name: c.club_name || c.course_name || course.club_name || course.course_name || "Imported course",
        subtitle: [c.location?.city, c.location?.country].filter(Boolean).join(", ") || "",
        teeBoxes,
        sourceId: String(course.id),
      });
      toast.success(`Imported "${created.name}" with ${teeBoxes.length} tee box${teeBoxes.length === 1 ? "" : "es"}`);
      setWebResults([]);
      setWebQuery("");
      await load();
      setExpanded(created.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(null); }
  };

  const importTee = (item, tee) => {
    const entry = {
      name: `${item.name} — ${tee.name}`,
      subtitle: item.subtitle || "",
      par: [...tee.par],
      si: [...tee.si],
      libraryId: item.id,
      teeId: tee.id,
      teeName: tee.name,
    };
    onImport(entry);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="library-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[color:var(--bg-dark-green)] border border-[color:var(--border-brass)] rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col tga-fade">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-brass-light)]">
          <div className="font-display text-xl text-[color:var(--brass-bright)]">Course Library</div>
          <button data-testid="library-close-btn" onClick={onClose} className="ghost-btn rounded-lg p-2"><X size={16} /></button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {/* Web search */}
          <div className="tga-card-dark p-3">
            <div className="flex items-center gap-2 mb-2 text-[color:var(--brass-bright)]">
              <Globe size={14} /><span className="font-display text-lg">Search the web</span>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="library-web-search-input"
                className="tga-input flex-1"
                placeholder="Course or club name"
                value={webQuery}
                onChange={(e) => setWebQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runWebSearch(); }}
              />
              <button data-testid="library-web-search-btn" className="brass-btn rounded-lg px-3 text-sm inline-flex items-center gap-1" onClick={runWebSearch} disabled={webLoading}>
                <Search size={14} /> {webLoading ? "…" : "Search"}
              </button>
            </div>
            {webResults.length > 0 && (
              <div className="mt-3 space-y-2">
                {webResults.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 border border-[color:var(--border-brass-light)] rounded-lg p-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{c.club_name || c.course_name}</div>
                      <div className="text-[11px] text-[color:var(--muted-green)] truncate">
                        {c.course_name && c.club_name !== c.course_name ? `${c.course_name} · ` : ""}
                        {[c.location?.city, c.location?.state, c.location?.country].filter(Boolean).join(", ")}
                      </div>
                    </div>
                    <button
                      data-testid={`library-web-import-${c.id}`}
                      disabled={importing === c.id}
                      className="brass-btn rounded-md px-3 py-1 text-xs inline-flex items-center gap-1"
                      onClick={() => importFromWeb(c)}
                    >
                      <Download size={12} /> {importing === c.id ? "…" : "Import"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <p className="helptext">Loading…</p>
          ) : (
            <>
              {items.length === 0 && !showNew && (
                <p className="helptext">Nothing saved yet. Add a course to reuse it across trips.</p>
              )}

              {items.map(item => (
                <div key={item.id} className="tga-card p-3">
                  <div className="flex items-center gap-2">
                    <button className="flex-1 text-left" onClick={() => setExpanded(expanded === item.id ? null : item.id)} data-testid={`library-item-${item.id}`}>
                      <div className="font-display text-lg leading-tight">{item.name}</div>
                      {item.subtitle && <div className="text-xs opacity-70">{item.subtitle}</div>}
                      <div className="text-[11px] text-[color:var(--brass-dark)] mt-0.5">
                        {item.teeBoxes.length} tee box{item.teeBoxes.length === 1 ? "" : "es"}{item.sourceId ? " · linked online" : ""}
                      </div>
                    </button>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      data-testid={`library-check-updates-${item.id}`}
                      className="ghost-btn rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1 flex-1 justify-center"
                      onClick={() => { setConfirmDelete(null); setUpdating(updating === item.id ? null : item.id); }}
                    >
                      <RefreshCw size={12} /> Check for updates
                    </button>
                    <button
                      data-testid={`library-remove-${item.id}`}
                      className="danger-btn rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1 !text-[#9b2c2c] !bg-[rgba(200,62,62,0.10)]"
                      onClick={() => { setUpdating(null); setConfirmDelete(confirmDelete === item.id ? null : item.id); }}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>

                  {confirmDelete === item.id && (
                    <div className="mt-2 border border-[rgba(180,40,40,0.4)] rounded-lg p-3 bg-[rgba(180,40,40,0.06)]" data-testid={`library-delete-confirm-${item.id}`}>
                      <div className="text-sm mb-2">Delete <span className="font-display">{item.name}</span> from the library? Trips already using it are not affected.</div>
                      <div className="flex gap-2">
                        <button data-testid={`library-delete-confirm-yes-${item.id}`} className="danger-btn rounded-md px-4 py-1.5 text-sm !bg-[#9b2c2c] !text-white" onClick={() => removeCourse(item.id)}>Yes, delete</button>
                        <button data-testid={`library-delete-confirm-no-${item.id}`} className="ghost-btn rounded-md px-4 py-1.5 text-sm" onClick={() => setConfirmDelete(null)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {updating === item.id && (
                    <CourseUpdatePanel
                      item={item}
                      onClose={() => setUpdating(null)}
                      onApplied={(updated) => setItems(prev => prev.map(x => x.id === updated.id ? updated : x))}
                      onPushed={(lib) => updateEventLocal?.((ev) => { syncTripCoursesFromLibrary(ev, lib); return ev; })}
                    />
                  )}

                  {expanded === item.id && (
                    <div className="mt-3 space-y-3">
                      {item.teeBoxes.map(tee => (
                        <div key={tee.id} className="border border-[rgba(27,51,34,0.15)] rounded-lg p-2">
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              data-testid={`library-tee-name-${tee.id}`}
                              className="tga-input-light flex-1 !py-1"
                              value={tee.name}
                              onChange={(e) => updateTeeName(item, tee.id, e.target.value)}
                            />
                            <button
                              data-testid={`library-import-tee-${tee.id}`}
                              className="brass-btn rounded-lg px-3 py-1 text-xs inline-flex items-center gap-1"
                              onClick={() => importTee(item, tee)}
                            >
                              <Download size={12} /> Use in trip
                            </button>
                            <button
                              data-testid={`library-delete-tee-${tee.id}`}
                              className="danger-btn rounded-lg p-2"
                              onClick={() => deleteTeeBox(item, tee.id)}
                            ><Trash2 size={12} /></button>
                          </div>
                          <div className="grid grid-cols-6 gap-1 font-mono-metrics text-xs">
                            {tee.par.map((par, i) => (
                              <div key={i} className="border border-[rgba(27,51,34,0.15)] rounded p-1 text-center">
                                <div className="text-[9px] uppercase opacity-70">H{i + 1}</div>
                                <input
                                  data-testid={`library-tee-par-${tee.id}-${i}`}
                                  type="number" min="3" max="6"
                                  className="tga-input-light w-full !p-1 text-center"
                                  value={par}
                                  onChange={(e) => updateTeeCell(item, tee.id, "par", i, e.target.value)}
                                />
                                <input
                                  data-testid={`library-tee-si-${tee.id}-${i}`}
                                  type="number" min="1" max="18"
                                  className="tga-input-light w-full !p-1 text-center mt-1"
                                  value={tee.si[i]}
                                  onChange={(e) => updateTeeCell(item, tee.id, "si", i, e.target.value)}
                                />
                              </div>
                            ))}
                          </div>
                          <p className="helptext text-[color:var(--text-dark)]/60 mt-1 text-[11px]">Top = Par · Bottom = Stroke Index</p>
                        </div>
                      ))}
                      <button data-testid={`library-add-tee-${item.id}`} className="ghost-btn rounded-lg w-full py-2 text-xs inline-flex items-center justify-center gap-1" onClick={() => addTeeBox(item)}>
                        <Plus size={12} /> Add tee box
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {showNew && (
                <div className="tga-card">
                  <div className="font-display text-lg mb-2">{initialSaveCourse ? "Save trip course to library" : "New course"}</div>
                  <label className="block text-xs uppercase text-[color:var(--brass-dark)] mb-1">Name</label>
                  <input data-testid="library-new-name" className="tga-input-light w-full" placeholder="e.g. St Andrews" value={newName} onChange={(e) => setNewName(e.target.value)} />
                  <label className="block text-xs uppercase text-[color:var(--brass-dark)] mt-2 mb-1">Subtitle (optional)</label>
                  <input data-testid="library-new-subtitle" className="tga-input-light w-full" placeholder="Old Course · Fife" value={newSubtitle} onChange={(e) => setNewSubtitle(e.target.value)} />
                  <label className="block text-xs uppercase text-[color:var(--brass-dark)] mt-2 mb-1">First tee box name</label>
                  <input data-testid="library-new-tee-name" className="tga-input-light w-full" placeholder="Blue" value={newFirstTee} onChange={(e) => setNewFirstTee(e.target.value)} />
                  <div className="flex gap-2 mt-3">
                    <button data-testid="library-new-save" disabled={saving} className="brass-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1" onClick={createNew}>
                      <Save size={14} /> {saving ? "Saving…" : "Save to library"}
                    </button>
                    <button className="ghost-btn rounded-lg px-4 py-2 text-sm" onClick={() => setShowNew(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {!showNew && (
          <div className="p-3 border-t border-[color:var(--border-brass-light)] flex gap-2">
            <button data-testid="library-add-new-btn" className="brass-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1 flex-1 justify-center" onClick={() => { setShowNew(true); setNewName(""); setNewSubtitle(""); setNewFirstTee("Blue"); }}>
              <Plus size={14} /> New course
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
